/**
 * `device-cache.md` → **"Purge and recovery rules"**, one function per rule.
 *
 * They are gathered here rather than inlined at their call sites because the rules differ in
 * *scope* in ways that are easy to get backwards, and each mistake is one-directional:
 *
 *  - **Membership removal** takes one gate out of the snapshot and **must not end unrelated
 *    device sessions** — over-purging here signs a user out of a phone they still hold.
 *  - **Sign-out / revoke / eviction** takes the credentials *and* the snapshot of the account
 *    that left, and **must not touch another account's row** — over-purging is merely rude,
 *    under-purging leaves a signed-out user's gates on a shared device.
 *  - **Privacy erasure** takes everything, including rows this module does not know about,
 *    which is why it destroys the database instead of enumerating keys.
 */

import { clearAccessToken, clearCredentials, clearRefreshMarker, clearResumeHint } from '../auth/credentials'
import { usesNativeCredentialStore } from '../auth/nativeCredentials'
import { discardAllCredentials } from '../ble/credentials'
import { purgeAccountOutbox, purgeOtherAccountOutboxes } from '../ble/outbox'
import { profileKey } from './profile'
import { snapshotKey } from './snapshot'
import type { GateSnapshot } from './snapshot'
import type { KeyValueStore } from './store'

/**
 * **Sign-out**, and equally **revoke / LRU eviction / administrative removal once detected**:
 * *"clear refresh credentials and the session binding … Also clear the signed-out user's gate
 * snapshot and unsent non-audit drafts on a shared device."*
 *
 * The resume hint goes too: every path here has already ended the server-side record, so an
 * id pointing at it can only fail to resolve.
 *
 * **D152 adds the two per-tab pieces beside the credentials they were derived from**: the
 * cached access token (`sessionStorage`) and the durable in-flight refresh marker, so a caller
 * that reaches this function directly — not only `SessionManager.endSession()`, which also
 * clears the token itself for the case this function never runs (no known `userId`) — gets the
 * same guarantee `clearCredentials` does.
 */
export async function purgeAccount(store: KeyValueStore, stage: string, userId: string): Promise<void> {
  await clearCredentials(store, stage)
  await clearRefreshMarker(store, stage)
  clearResumeHint(stage)
  clearAccessToken(stage)
  await store.remove('snapshots', snapshotKey(stage, userId))
  await store.remove('snapshots', profileKey(stage, userId))
  /*
   * **P16 — the signed-out account's queued receipts go too**, and this is the one purge rule
   * where a receipt behaves like the snapshot rather than like an audit record.
   *
   * `device-cache.md` protects a receipt from *gate removal* (*"preserve … long enough to
   * reconcile"*) and destroys it on *erasure* (D69), and neither of those is this. This is
   * sign-out on a shared device — the case the snapshot rule already covers with *"unsent
   * non-audit drafts"* — and a receipt is stronger evidence about a person than a gate name is:
   * it says who opened which gate at which minute. 🔸 The accepted cost is that signing out with
   * an unsynced receipt loses that Activity row; the alternative leaves one person's movements
   * on a phone they have handed back. See `ble/outbox.ts` for the same note beside the code.
   */
  await purgeAccountOutbox(store, stage, userId)
  // *"…discard when … authorization fails"*. A revoke, an eviction, and a sign-out are all that,
  // and a credential surviving one would be a gate password held by a session that no longer
  // exists — briefly usable, since the controller has never heard of Cognito.
  discardAllCredentials()
}

/**
 * **Account switch.** Signing in as someone else must not leave the previous account's gates
 * readable, and the credentials row is keyed by stage alone, so the *snapshot* is what needs
 * removing by id (startup contract step 2 — "never show another account's snapshot").
 *
 * **The receipts go FIRST, and the order is what makes {@link hasOtherAccountRows} a sufficient
 * check.** A run that stops part-way — the 2026-10-16 dead connection — then always leaves at least
 * one other account's row in `snapshots`, so the boot backstop can tell from one `keys()` read
 * whether a purge is still owed, instead of scanning both stores on every launch.
 *
 * `proceed` is consulted before every removal: the boot backstop runs in the background, and a
 * sign-out followed by another account's sign-in while it is still walking keys would otherwise
 * make the new account's fresh rows "other accounts".
 */
export async function purgeOtherAccounts(
  store: KeyValueStore,
  stage: string,
  keepUserId: string,
  proceed: () => boolean = () => true,
): Promise<void> {
  // The receipts follow the snapshot, for the reason startup step 2 gives: never show — or hold
  // — another account's record.
  await purgeOtherAccountOutboxes(store, stage, keepUserId, proceed)
  for (const key of await store.keys('snapshots')) {
    if (!isOtherAccountKey(key, stage, keepUserId)) continue
    if (!proceed()) return
    await store.remove('snapshots', key)
  }
}

/** Whether a {@link purgeOtherAccounts} run is still owed — see its note on ordering. */
export async function hasOtherAccountRows(
  store: KeyValueStore,
  stage: string,
  keepUserId: string,
): Promise<boolean> {
  return (await store.keys('snapshots')).some((key) => isOtherAccountKey(key, stage, keepUserId))
}

function isOtherAccountKey(key: string, stage: string, keepUserId: string): boolean {
  if (key === snapshotKey(stage, keepUserId) || key === profileKey(stage, keepUserId)) return false
  return key.startsWith(`${stage}:`) || key.startsWith(`profile:${stage}:`)
}

/**
 * **Membership removal / gate removal:** *"remove that gate from the refreshed snapshot …
 * Do not end unrelated device sessions."* Applied to the snapshot in memory before it is
 * written, so a gate the server stopped returning also stops being rendered — the same rule
 * from the snapshot row: *"Remove gates that the server no longer returns."*
 */
export function withoutGate(snapshot: GateSnapshot, gateId: string): GateSnapshot {
  return { ...snapshot, gates: snapshot.gates.filter((gate) => gate.gateId !== gateId) }
}

/**
 * **Privacy erasure**, and the corruption/quota fallback: *"delete every user-scoped local
 * record and secret after the server accepts the erasure request."*
 *
 * **D69's client half, and it needs no new line.** *"An unsynced completed-BLE receipt is
 * discarded, not reconciled"* — `store.destroy()` takes the whole database, `outbox` included, so
 * the receipt is destroyed unsynced exactly as the decision requires. That is why this function
 * destroys rather than enumerating keys: it reaches rows this module does not know about, and
 * P16's arrival is the case that proves the choice rather than a reason to revisit it.
 *
 * **The server half is the receipt upload's own tombstone check** — a receipt that escaped this
 * purge and arrives later is discarded there, leaving only a non-identifying counter. The two
 * halves are independent on purpose: a device that never comes back cannot run this one, and a
 * device that never uploads cannot reach the other.
 *
 * **Local deletion logs nothing** (PEC-43). No count, no gate id, no instant on the way out —
 * which is also why this returns `void` rather than a summary somebody would eventually render.
 */
export async function purgeEverything(store: KeyValueStore, stage: string): Promise<void> {
  // **D339 — in the container the credential row is not in the database this destroys**; it is in
  // the keystore, so it is cleared by name first. The browser PWA's row goes with `destroy()` below,
  // exactly as before. **A keystore clear that fails must not cost the rest of the erasure**, so it
  // is attempted first and its failure rethrown only once `destroy()` has run; the pending-clear
  // flag `clearCredentials` leaves finishes it on the next launch.
  let keystoreFailure: { readonly error: unknown } | undefined
  if (usesNativeCredentialStore()) {
    try {
      await clearCredentials(store, stage)
    } catch (error) {
      keystoreFailure = { error }
    }
  }
  clearResumeHint(stage)
  clearAccessToken(stage)
  discardAllCredentials()
  await store.destroy()
  if (keystoreFailure !== undefined) throw keystoreFailure.error
}
